import { useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../app/store';
import { addPendingChange, removeOperationFromPending } from '../patchesSlice';
import { buildPendingEntry, wrapOperationNode, wrapOperationNodeForRemoval } from '../utils/pendingOperations';
import {
  ExclusionSearchResult,
  ExclusionState,
  ExclusionTarget,
  isTargetComplete,
} from '../../../services/exclusions/exclusions.types';
import { useExclusionSearch } from '../../../services/exclusions/exclusions.service';

/**
 * What to do about one store.
 *
 * `none` is a real choice, not just an initial value: the point of a per-row review is that a store found
 * in an unexpected state can be left alone.
 */
export type ReconcileDecision = 'none' | 'add' | 'remove';

export interface ReconcileRow {
  storeNumber: string;
  patchId: string;
  state: ExclusionState;
  decision: ReconcileDecision;
  /**
   * Other stores this row's patch also serves, which are not in the search.
   *
   * Carried per row rather than derived once, because two rows can share a patch and each needs to show
   * its own reach - and a shared patch means one decision here changes stores the user never listed.
   */
  otherStoreNumbers: string[];
  /** The backend could not see every assignment, so `otherStoreNumbers` is a floor, not a total. */
  otherStoresTruncated: boolean;
  /**
   * The decisions this row can take.
   *
   * State-dependent because the operations are not symmetric. A store with no operation can only gain
   * one; a store that has it can only lose it. The three-valued case is the interesting one: an operation
   * present with `isExcludedFromVariant: false` can either be flipped to true (add) or deleted entirely
   * (remove), and those are genuinely different outcomes, so the user picks.
   */
  available: ReconcileDecision[];
}

/**
 * Entity objects for the chosen target, when the picker has them.
 *
 * Ids alone are enough to build a correct request, but the pending-changes list reads names off these
 * objects and falls back to showing a UUID without them. Optional so the hook stays usable from a test or
 * a flow that only has ids.
 */
export interface ExclusionTargetEntities {
  product?: any;
  variant?: any;
  slot?: any;
  modifier?: any;
  weight?: any;
  bundle?: any;
}

const decisionsFor = (state: ExclusionState): ReconcileDecision[] => {
  switch (state) {
    case 'absent':
      return ['none', 'add'];
    case 'excluded':
      return ['none', 'remove'];
    case 'explicitly_not_excluded':
      return ['none', 'add', 'remove'];
    default:
      return ['none'];
  }
};

/**
 * Builds a synthetic patch operation node for a target.
 *
 * Deliberately shaped like a `getPatch` node so the existing wrappers can consume it: that keeps one
 * definition of every request payload in `toMinimalOperation` rather than restating the seven exclusion
 * shapes here, where they could drift. Entity objects are nested when the picker supplied them - both
 * because the enriched half reads names off them, and because the id readers check the nested path first.
 */
export const buildExclusionNode = (target: ExclusionTarget, entities: ExclusionTargetEntities = {}): any => {
  const node: any = { __typename: target.operationType };

  if (target.productId) node.product = entities.product ?? { productId: target.productId };
  if (target.variantId) node.variant = entities.variant ?? { variantId: target.variantId };
  if (target.slotId) node.slot = entities.slot ?? { slotId: target.slotId };
  if (target.modifierId) node.modifier = entities.modifier ?? { modifierId: target.modifierId };
  if (target.bundleId) node.bundle = entities.bundle ?? { bundleId: target.bundleId };
  if (target.weightId) {
    // readWeightId checks modifierWeightId first, which is the response spelling.
    node.weight = entities.weight ?? { modifierWeightId: target.weightId };
  }

  // The one type whose payload carries the answer rather than implying it by existing. Adding means
  // asserting true; the false case is only ever something the search found, never something staged.
  if (target.operationType === 'SetModifierWeightIsExcludedFromVariantMenuPatchOperation') {
    node.isExcludedFromVariant = true;
  }

  return node;
};

/**
 * The bulk exclusion reconcile: search one target across a store set, decide per store, stage the result.
 *
 * Kept out of useBulkUpdate, which is already 800+ lines carrying several flows. Composed by the page
 * instead, the way useSubmitPatches and usePatchDataExtractor are.
 *
 * Decisions become exactly two pending entries, both `all`-scoped over the patch ids that got each
 * decision - never one entry per store. `resolveOperationPlan` groups by the set of opIds a patch
 * receives, so per-store entries would mean one request per store; two subset entries mean two requests
 * whether the reconcile covers 5 stores or the whole estate.
 */
export const useExclusionWorkflow = () => {
  const dispatch = useDispatch<AppDispatch>();

  const [target, setTargetState] = useState<ExclusionTarget | null>(null);
  const [entities, setEntities] = useState<ExclusionTargetEntities>({});
  const [decisions, setDecisions] = useState<Record<string, ReconcileDecision>>({});

  // Entries this hook put into the pending list, so re-staging replaces them instead of piling up.
  const stagedOpIds = useRef<string[]>([]);

  const { search, reset: resetSearch, isSearching, progress, data, error } = useExclusionSearch();

  const setTarget = useCallback(
    (next: ExclusionTarget | null, nextEntities: ExclusionTargetEntities = {}) => {
      setTargetState(next);
      setEntities(nextEntities);
      // A different target makes every existing answer and decision meaningless.
      setDecisions({});
      resetSearch();
    },
    [resetSearch]
  );

  /**
   * Searches the selected stores.
   *
   * Store numbers, not patch ids: the page's view of a store's patches comes from the store payload, whose
   * patch list is scoped to the ECOMM menu channel, so it cannot see a patch assigned elsewhere. The
   * backend resolves each store's patch of the relevant type instead, and reports the ones it could not.
   */
  const runSearch = useCallback(
    async (storeNumbers: string[], explicitTarget?: ExclusionTarget | null) => {
      setDecisions({});
      // The caller may pass the target it is holding rather than relying on setTarget having landed in
      // state first. Those are two renders apart, and making the search depend on that ordering is how a
      // complete form ends up unable to search.
      return search(storeNumbers, explicitTarget ?? target);
    },
    [search, target]
  );

  const rows = useMemo<ReconcileRow[]>(() => {
    const results: ExclusionSearchResult[] = data?.results ?? [];

    return results.map((result) => ({
      storeNumber: result.storeNumber,
      patchId: result.patchId,
      state: result.state,
      decision: decisions[result.storeNumber] ?? 'none',
      otherStoreNumbers: result.otherStoreNumbers ?? [],
      otherStoresTruncated: result.otherStoresTruncated ?? false,
      available: decisionsFor(result.state),
    }));
  }, [data, decisions]);

  const setDecision = useCallback((storeNumber: string, decision: ReconcileDecision) => {
    setDecisions((previous) => ({ ...previous, [storeNumber]: decision }));
  }, []);

  /**
   * Applies a decision to every row whose state matches.
   *
   * `explicitly_not_excluded` is reachable by neither helper on purpose - both add and remove are
   * defensible for it, so sweeping it up either way would be the tool deciding something the user should.
   *
   * `scope` narrows the sweep to a set of stores, which is how the table keeps a filtered view honest: a
   * button offered under a filter should not reach past what the filter shows. Optional rather than
   * required because an unfiltered table means every searched store, and that is the common case.
   */
  const selectAll = useCallback(
    (state: ExclusionState, decision: ReconcileDecision, scope?: string[]) => {
      const inScope = scope ? new Set(scope) : null;

      setDecisions((previous) => {
        const next = { ...previous };
        (data?.results ?? []).forEach((result) => {
          if (inScope && !inScope.has(result.storeNumber)) return;
          if (result.state === state) next[result.storeNumber] = decision;
        });
        return next;
      });
    },
    [data]
  );

  const selectAllMissing = useCallback((scope?: string[]) => selectAll('absent', 'add', scope), [selectAll]);
  const selectAllFound = useCallback((scope?: string[]) => selectAll('excluded', 'remove', scope), [selectAll]);
  const clearDecisions = useCallback(() => setDecisions({}), []);

  /**
   * Who a patch here would reach beyond the searched stores, as found and as staged.
   *
   * Two summaries rather than one, because they answer different questions and the answers matter at
   * different moments. `found` is the overview: it exists as soon as results arrive, so sharing is
   * something the user is told rather than something they have to notice on whichever page they happen
   * to be looking at. `staged` is the consequence of the decisions actually taken, which is what turns
   * the notice into a warning.
   */
  const reach = useMemo(() => {
    const summarise = (include: (row: ReconcileRow) => boolean) => {
      const stores = new Set<string>();
      let patches = 0;
      let truncated = false;

      rows.forEach((row) => {
        if (!include(row)) return;
        // Set before the emptiness check: a truncated page means there may be stores we never saw, so
        // even an empty list is a floor rather than a total.
        if (row.otherStoresTruncated) truncated = true;
        if (!row.otherStoreNumbers.length) return;

        patches += 1;
        row.otherStoreNumbers.forEach((storeNumber) => stores.add(storeNumber));
      });

      return { stores: Array.from(stores).sort(), patches, truncated };
    };

    return {
      found: summarise(() => true),
      staged: summarise((row) => row.decision !== 'none'),
    };
  }, [rows]);

  const counts = useMemo(() => {
    const byState = { absent: 0, excluded: 0, explicitly_not_excluded: 0 };
    rows.forEach((row) => {
      byState[row.state] += 1;
    });

    return {
      ...byState,
      toAdd: rows.filter((row) => row.decision === 'add').length,
      toRemove: rows.filter((row) => row.decision === 'remove').length,
      unreadable: data?.unreadable.length ?? 0,
      failed: data?.failed.length ?? 0,
    };
  }, [rows, data]);

  /**
   * Writes the decisions into the pending changes list.
   *
   * Returns how many requests the submission will take, so the review screen can say so before the user
   * commits - two entries here, but the submit path chunks large patch lists, and a whole-estate
   * reconcile is many requests even though it is one decision.
   */
  const stageDecisions = useCallback((): { setPatchIds: string[]; removePatchIds: string[] } | null => {
    if (!isTargetComplete(target)) return null;

    const setPatchIds = rows.filter((row) => row.decision === 'add').map((row) => row.patchId);
    const removePatchIds = rows.filter((row) => row.decision === 'remove').map((row) => row.patchId);

    // Withdraw anything staged earlier before deciding whether there is anything to stage now.
    // Ordering matters: clearing every decision has to withdraw the previous entries, not leave them
    // behind - otherwise "Clear" would look like it worked while the old operations were still queued
    // for submission.
    stagedOpIds.current.forEach((opId) => dispatch(removeOperationFromPending({ opId })));
    stagedOpIds.current = [];

    if (!setPatchIds.length && !removePatchIds.length) return null;

    const node = buildExclusionNode(target, entities);

    const setEntry = setPatchIds.length
      ? (() => {
          const wrapper = wrapOperationNode(node);
          return wrapper ? buildPendingEntry(wrapper, { kind: 'all', patchIds: setPatchIds }) : null;
        })()
      : null;

    const removeEntry = removePatchIds.length
      ? (() => {
          const wrapper = wrapOperationNodeForRemoval(node);
          return wrapper ? buildPendingEntry(wrapper, { kind: 'all', patchIds: removePatchIds }) : null;
        })()
      : null;

    if (!setEntry && !removeEntry) return null;

    dispatch(
      addPendingChange({
        type: 'Exclusion Reconcile',
        data: {
          setOperations: setEntry ? [setEntry] : [],
          removeOperations: removeEntry ? [removeEntry] : [],
        },
      })
    );

    stagedOpIds.current = [setEntry?.opId, removeEntry?.opId].filter(Boolean) as string[];

    return {
      setPatchIds: setEntry ? setPatchIds : [],
      removePatchIds: removeEntry ? removePatchIds : [],
    };
  }, [dispatch, entities, rows, target]);

  const reset = useCallback(() => {
    stagedOpIds.current.forEach((opId) => dispatch(removeOperationFromPending({ opId })));
    stagedOpIds.current = [];
    setTargetState(null);
    setEntities({});
    setDecisions({});
    resetSearch();
  }, [dispatch, resetSearch]);

  return {
    target,
    setTarget,
    isTargetReady: isTargetComplete(target),

    runSearch,
    isSearching,
    progress,
    searchError: error,
    hasSearched: !!data,

    rows,
    // Reported so the user knows these were seen and could not be read, rather than silently absent from
    // the table or - worse - reported as "not excluded", which would be a claim about their contents.
    unreadable: data?.unreadable ?? [],
    failed: data?.failed ?? [],
    counts,
    // Stores a submission would change that the user never listed. See `reach` above.
    reach,

    setDecision,
    selectAllMissing,
    selectAllFound,
    clearDecisions,

    stageDecisions,
    reset,
  };
};
